/**
 * Hook wrapper around the add-players expand-request reactive variable (SE-6488).
 *
 * Mirrors `useStageEditorPanelState` / `useRulesPanelState`: the header's
 * `+ Add player` CTA calls `requestExpand(boardId)`, and the add-players rail
 * folds `expandKey` into its key-reset key so it remounts expanded.
 * @module features/player-kanban/hooks/useAddPlayersPanelState
 */

import { useReactiveVar } from "@apollo/client";
import { useCallback, useMemo } from "react";

import {
  addPlayersExpandRequestVar,
  requestAddPlayersExpand,
} from "../stores/addPlayersPanelStore";

/** Public API returned by {@link useAddPlayersPanelState}. */
export interface UseAddPlayersPanelStateResult {
  /**
   * Whether the rail should mount expanded for `boardId` — true only when the
   * latest request was made for that same board.
   * @param boardId - The board being rendered.
   * @returns Whether to start expanded.
   */
  readonly shouldStartExpanded: (boardId: string | null | undefined) => boolean;
  /**
   * Remount key contribution. Changes on every press so a repeat press
   * re-expands a rail the user has since collapsed.
   */
  readonly expandKey: number;
  /**
   * Ask the add-players rail to expand.
   * @param boardId - The pipeline whose rail should expand.
   */
  readonly requestExpand: (boardId: string | null | undefined) => void;
}

/**
 * Subscribe to the add-players expand request plus its imperative helper.
 * @returns The {@link UseAddPlayersPanelStateResult}.
 */
export function useAddPlayersPanelState(): UseAddPlayersPanelStateResult {
  const request = useReactiveVar(addPlayersExpandRequestVar);

  const requestExpand = useCallback(
    (boardId: string | null | undefined) =>
      requestAddPlayersExpand(boardId ?? null),
    []
  );

  const shouldStartExpanded = useCallback(
    (boardId: string | null | undefined) =>
      request.requestId > 0 && request.boardId === (boardId ?? null),
    [request]
  );

  return useMemo(
    () => ({
      shouldStartExpanded,
      expandKey: request.requestId,
      requestExpand,
    }),
    [shouldStartExpanded, request.requestId, requestExpand]
  );
}
